import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { ErrorHandlingService } from '../../error-handling/error-handling.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { SpinnerService } from '../../spinner';
import { Observable, of } from 'rxjs';
import { HttpHeaderConstants } from '../../http-header.constant';
import { catchError, finalize } from 'rxjs/operators';
import { ResponsysPackageDetail } from '../../../models/appcloud/responsys-detail.model';

@Injectable({
    providedIn: 'root',
})
export class ResponsysPackageDetailService {
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private RESPONSYS_PACKAGE_DETAILS_URL: string = environment.publicApiHost + '/appcloud/responsys-package-details';

    getByPackageId(appId: number, additionalHeaders: any = {}): Observable<ResponsysPackageDetail[]> {
        this.#spinnerService.addProcess(
            'ResponsysPackageDetailService.getByPackageId',
            'Loading Responsys package details...'
        );
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('appPackageId', appId.toString()),
        };

        return this.#http.get<ResponsysPackageDetail[]>(this.RESPONSYS_PACKAGE_DETAILS_URL, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving Responsys package data from Relationship One. Please try again later.'
                );
                return of<ResponsysPackageDetail[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess('ResponsysPackageDetailService.getByPackageId'))
        );
    }

    create(
        responsysPackageDetail: ResponsysPackageDetail,
        additionalHeaders: any = {}
    ): Observable<ResponsysPackageDetail> {
        this.#spinnerService.addProcess(
            'ResponsysPackageDetailService.create',
            'Creating Responsys package details...'
        );
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http
            .post<ResponsysPackageDetail>(this.RESPONSYS_PACKAGE_DETAILS_URL, responsysPackageDetail, httpOptions)
            .pipe(
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while creating Responsys package details. Please try again later.'
                    );
                    return of<ResponsysPackageDetail>();
                }),
                finalize(() => this.#spinnerService.completeProcess('ResponsysPackageDetailService.create'))
            );
    }

    update(
        id: number,
        responsysPackageDetail: ResponsysPackageDetail,
        additionalHeaders: any = {}
    ): Observable<ResponsysPackageDetail> {
        this.#spinnerService.addProcess(
            'ResponsysPackageDetailService.update',
            'Updating Responsys package details...'
        );
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.mergePatch,
                ...additionalHeaders,
            }),
        };

        return this.#http
            .patch<ResponsysPackageDetail>(
                this.RESPONSYS_PACKAGE_DETAILS_URL + '/' + id,
                responsysPackageDetail,
                httpOptions
            )
            .pipe(
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while updating Responsys package details. Please try again later.'
                    );
                    return of<ResponsysPackageDetail>();
                }),
                finalize(() => this.#spinnerService.completeProcess('ResponsysPackageDetailService.update'))
            );
    }
}
